/**
 * US-format (MM/DD/YYYY) date validation shared across forms.
 *
 * The regex used by earlier schemas only checked the *shape* of the
 * string — values like "02/30/2025" passed because day ≤ 31 satisfies
 * the digit class. They reached the backend, which then 422'd with
 * "day value is outside expected range". This helper additionally
 * confirms that the (year, month, day) trio represents a real
 * calendar date (e.g. Feb 30 fails because constructing it via the
 * Date primitive rolls over to March 2).
 *
 * Use it the same way regardless of whether the field is required:
 *
 *   const optionalDate = z.string().refine(isEmptyOrValidUsDate, INVALID_DATE_MESSAGE);
 *   const requiredDate = z.string().min(1, REQUIRED).refine(isValidUsDate, INVALID_DATE_MESSAGE);
 */

/** Single source of truth for the format-hint message — never echoes
 *  the user's input back at them. */
export const INVALID_DATE_MESSAGE = "Enter a valid date (MM/DD/YYYY)";

// Strict MM/DD/YYYY shape: month and day must be zero-padded two-digit
// values (01–12 / 01–31), year a 4-digit number. Single-digit shorthand
// like `1/2/2025` is rejected — earlier the lax `0?` allowed it through
// and one-digit years like `01/12/2` reached the backend.
const FORMAT_RE = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(\d{4})$/;

/** Returns true if `s` is a real calendar date written as MM/DD/YYYY. */
export function isValidUsDate(s: string): boolean {
    const m = s.match(FORMAT_RE);
    if (!m) return false;
    const month = Number(m[1]);
    const day = Number(m[2]);
    const year = Number(m[3]);
    const d = new Date(year, month - 1, day);
    // JS Date silently rolls invalid combinations forward (Feb 30 →
    // Mar 2). We re-read the components and demand they match what
    // the user typed.
    return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;
}

/** Empty input is treated as "no value" — the caller layers `min(1)`
 *  on top when the field is required. Useful for optional date fields
 *  so they don't trip on a blank cell. */
export function isEmptyOrValidUsDate(s: string): boolean {
    return s === "" || isValidUsDate(s);
}

/** Today (local time) as MM/DD/YYYY — for defaulting date fields like
 *  the Date of Submission on a new submission. */
export function todayUsDate(): string {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${mm}/${dd}/${d.getFullYear()}`;
}
